import { observer } from 'mobx-react-lite';
import React, { forwardRef, InputHTMLAttributes, useState } from 'react';
import ClosedEyeIcon from '../icons/ClosedEyeIcon';
import OpenEyeIcon from '../icons/OpenEyeIcon';
import { getInputClassName } from './helpers/getInputClassName';

export interface IInputProps extends InputHTMLAttributes<HTMLInputElement> {
  variant?: 'default' | 'without-borders' | 'error' | 'success';
  scale?: 'small' | 'fit-content' | 'default';
  numeric?: boolean;
}

export const Input = observer(
  forwardRef<HTMLInputElement, IInputProps>(
    (
      {
        variant = 'default',
        scale = 'default',
        numeric = false,
        className,
        readOnly,
        type,
        onChange,
        ...props
      },
      ref
    ) => {
      const [showPassword, setShowPassword] = useState(false);

      const isPasswordField = type === 'password';
      const inputType = isPasswordField && showPassword ? 'text' : type;

      const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (numeric) {
          let value = e.target.value;

          value = value.replace(/\./g, ',');
          value = value.replace(/[^0-9,]/g, '');

          const parts = value.split(',');
          if (parts.length > 2) {
            value = parts[0] + ',' + parts.slice(1).join('');
          }

          e.target.value = value;
        }

        if (onChange) {
          onChange(e);
        }
      };

      const numericAttributes = numeric
        ? {
            inputMode: 'decimal' as const,
            pattern: '[0-9]*[.,]?[0-9]*',
          }
        : {};

      if (isPasswordField) {
        return (
          <div
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <input
              className={getInputClassName({
                variant,
                scale,
                className,
                disabled: props.disabled,
              })}
              ref={ref}
              type={inputType}
              disabled={readOnly}
              onChange={handleChange}
              {...numericAttributes}
              {...props}
            />
            {!readOnly && (
              <span
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {showPassword ? <ClosedEyeIcon /> : <OpenEyeIcon />}
              </span>
            )}
          </div>
        );
      }

      return (
        <input
          className={getInputClassName({
            variant,
            scale,
            className,
            disabled: props.disabled,
          })}
          ref={ref}
          readOnly={readOnly}
          type={type}
          onChange={handleChange}
          {...numericAttributes}
          {...props}
        />
      );
    }
  )
);

Input.displayName = 'Input';
